import { TRANSLATION_LOCALES, type ContentLocale } from '@/features/cms/lib/contentLocales';
import { pickLocaleHtml } from '@/features/cms/lib/localeContent';
import type { PolicyTranslation } from '../types/policy';

/**
 * 정책 해외몰 문구(catalog V127) — 폼의 로케일별 입력 ↔ API translations 변환.
 *
 * 공용 `buildLocaleHtmlTranslations` 를 쓰지 않는 이유: 그 헬퍼는 빈 로케일을 빼고 필드 하나만 채운다.
 * 정책 API 는 받은 로케일의 name·content 를 둘 다 덮어쓰므로(null 도 저장),
 *   - 필드 하나만 보내면 다른 필드 번역이 지워지고,
 *   - 빈 로케일을 빼면 지운 번역이 서버에 그대로 남는다.
 * 그래서 보낼 로케일은 항상 두 필드를 문자열로 채우고, 지운 로케일은 빈 문자열로 보내 국문 폴백시킨다.
 */

export type LocaleTextMap = Partial<Record<ContentLocale, string>>;

export interface PolicyTranslationForm {
  nameTranslations: LocaleTextMap;
  contentTranslations: LocaleTextMap;
}

/** 응답 translations → 폼 입력. 빈 값·null 은 빠진다. */
export const pickPolicyTranslations = (
  translations: Readonly<Record<string, PolicyTranslation>> | null | undefined,
): PolicyTranslationForm => ({
  nameTranslations: pickLocaleHtml(translations, 'name'),
  contentTranslations: pickLocaleHtml(translations, 'content'),
});

const hasText = (v: string | null | undefined): boolean => typeof v === 'string' && v.trim() !== '';

/**
 * 폼 입력 → 요청 translations. 보내는 로케일:
 *   - 이름이나 문구 중 하나라도 입력된 로케일
 *   - 서버에 번역이 있었는데 이번에 비운 로케일(빈 문자열로 보내 지운다)
 * 보낼 로케일이 없으면 undefined — 요청에서 생략하면 BE 가 서버 번역을 그대로 둔다(등록은 번역 없음).
 *
 * @param previous 수정 시 불러온 서버 translations. 등록이면 생략.
 */
export const buildPolicyTranslations = (
  form: PolicyTranslationForm,
  previous?: Readonly<Record<string, PolicyTranslation>> | null,
): Record<string, PolicyTranslation> | undefined => {
  const entries: [string, PolicyTranslation][] = [];
  for (const loc of TRANSLATION_LOCALES) {
    const name = form.nameTranslations[loc]?.trim() ?? '';
    const content = hasText(form.contentTranslations[loc]) ? (form.contentTranslations[loc] as string) : '';
    const before = previous?.[loc];
    const hadBefore = hasText(before?.name) || hasText(before?.content);
    if (name === '' && content === '' && !hadBefore) continue;
    entries.push([loc, { name, content }]);
  }
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};
